// Нижняя шторка на shadcn Drawer (ветка Vaul). Фокус ловится внутри и
// возвращается на кнопку-триггер (Radix Dialog), Escape и жест вниз закрывают,
// содержимое прокручивается, футер с действием прижат над safe area и клавиатурой.
//
// Клавиатура: встроенный repositionInputs у Vaul на iOS двигает шторку рывками.
// Вместо него шторка ставится над клавиатурой по visualViewport (--sheet-kb) и
// ограничивается видимой высотой (--sheet-vh); поле в фокусе прокручивается
// внутри шторки, а не вся страница.
import { useEffect, useRef, type ReactNode } from 'react';

function useKeyboardInset(open: boolean, ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!open || !vv) return;
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      el.style.setProperty('--sheet-kb', `${Math.round(inset)}px`);
      el.style.setProperty('--sheet-vh', `${Math.round(vv.height)}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, [open, ref]);
}
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  headerStart,
  focusKey,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  headerStart?: ReactNode;
  /** Меняется при смене шага: фокус переводится на заголовок шага */
  focusKey?: string;
  className?: string;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useKeyboardInset(open, contentRef);
  useEffect(() => {
    if (open && focusKey) {
      const id = requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));
      return () => cancelAnimationFrame(id);
    }
  }, [open, focusKey]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        ref={contentRef}
        className={cn(
          'mx-auto w-full max-w-xl border-border bg-surface',
          'data-[vaul-drawer-direction=bottom]:bottom-[var(--sheet-kb,0px)]',
          'data-[vaul-drawer-direction=bottom]:max-h-[min(92dvh,calc(var(--sheet-vh,100dvh)-12px))]',
          className,
        )}
      >
        <DrawerHeader className="flex-row items-center gap-2 px-4 pb-2 text-left md:text-left">
          {headerStart}
          <div className="min-w-0 flex-1">
            <DrawerTitle ref={titleRef} tabIndex={-1} className="text-lg font-semibold text-primary outline-none">
              {title}
            </DrawerTitle>
            {description ? <DrawerDescription className="text-sm text-secondary">{description}</DrawerDescription> : null}
          </div>
        </DrawerHeader>
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
          onFocus={(e) => {
            const t = e.target as HTMLElement;
            if (t.matches('input, textarea')) {
              // после появления клавиатуры поле — в видимой части шторки
              setTimeout(() => t.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 350);
            }
          }}
        >
          {children}
        </div>
        {footer ? (
          <DrawerFooter className="border-t border-border bg-surface px-4 pt-3 safe-bottom">{footer}</DrawerFooter>
        ) : (
          <div className="safe-bottom" />
        )}
      </DrawerContent>
    </Drawer>
  );
}
